import { html, useState } from '/vendor/htm-preact.module.js';
import { post } from '/api.js';

/**
 * Asking whoever runs this instance for help. GitHub issue #67.
 *
 * Reached from the envelope in the lockup, beside the bell. A screen rather
 * than a modal, for the same reason Notifications is one: it is somewhere you
 * go and come back from, and a dialog over a broken screen is a second thing
 * to get out of when you are already stuck.
 *
 * The count is not decoration. `MAX` is bounded on the server as well (see
 * `Service.send_support_message`), and a box that lets somebody type nine
 * hundred characters and then refuses the lot has wasted their time and lost
 * their message — which, on a support form, is the one failure that cannot be
 * reported through the support form.
 */
const MAX = 500;

export function SupportScreen({ onDone }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [failed, setFailed] = useState('');

  const send = async () => {
    setBusy(true); setFailed('');
    try {
      await post('/api/support', { text });
      setText('');
      setSent(true);
    } catch (e) {
      setFailed(e.detail || e.message);
    } finally { setBusy(false); }
  };

  const left = MAX - text.length;

  return html`
    <div class="screen stack">
      <button class="secondary" onClick=${onDone}>Back</button>
      <label>Ask for help</label>
      <p class="sub" style="text-transform:none">
        This goes to whoever runs Cueforge. Say what happened and what you
        were doing — it reaches us with your name on it, so we can answer.
      </p>

      ${sent
        ? html`
          <p class="sub" data-testid="support-sent" style="text-transform:none">
            Sent. We will pick it up — you do not need to send it again.
          </p>
          <button class="secondary" data-testid="support-again"
                  onClick=${() => setSent(false)}>Say something else</button>`
        : html`
          <textarea data-testid="support-text" rows="6" maxlength=${MAX}
                    placeholder="What went wrong?"
                    value=${text}
                    onInput=${(e) => setText(e.target.value)}></textarea>
          <span class="sub" data-testid="support-left">
            ${`${left} characters left`}
          </span>
          ${/* Disabled on empty rather than refused on press: the server
                refuses it too, and being told off for a mis-tap is worse
                than the button simply not being ready. */''}
          <button data-testid="support-send"
                  disabled=${busy || !text.trim()}
                  onClick=${send}>Send</button>`}

      ${failed && html`
        <p class="error" data-testid="support-error">${failed}</p>`}
    </div>`;
}
